/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

// React & Third-party Libraries (NPM Packages)
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Image, Row } from 'react-bootstrap';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalculator,
    faEnvelope,
    faGlobe,
    faIdBadge,
    faMapMarkerAlt,
    faPhone,
    faSatelliteDish,
    faUser,
} from '@fortawesome/free-solid-svg-icons';
import { faFileCertificate } from '@fortawesome/pro-solid-svg-icons';

// Global App Config & Services
import { APP_COLORS } from '../../../../common/Constants';
import { SelectStyled } from '../../../../common/components/form/FormSelect';
import { getListImportOverlayRecord, removeCubeTableName } from '../../../../common/services/ListService';

// Redux Slices & Filters
import { setFilterValue } from '../../universeSlice';
import { countUniverse, searchUniverse } from '../../universeQuerySlice';
import { hiddenFilters } from '../../hiddenFilters';

// Local Components
import ExploreMapFlyoutDetailLoadingSpinner from './ExploreMapFlyoutDetailLoadingSpinner';
import { ExploreMapFlyoutSelectedDetailSection } from './ExploreMapFlyoutSelectedDetailSection';

// Assets
import mapMarkerIcon from '../../../../common/assets/img/ToolbeltIcons-15.png';

const options = [
    { value: 5, label: '5 Miles' },
    { value: 10, label: '10 Miles' },
    { value: 20, label: '20 Miles' },
    { value: 50, label: '50 Miles' },
    { value: 100, label: '100 Miles' },
    { value: 200, label: '200 Miles' },
];

export default function ExploreMapFlyoutDetailSelectedImportListLocation(props) {
    const dispatch = useDispatch();

    // Get Universe State
    const universeState = useSelector(state => state.universe);
    const currentUniverse = universeState.currentUniverse || 'BUSINESS';
    const targetUniverse = universeState.universes[currentUniverse];

    const [loading, setLoading] = useState(true);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedDistanceFrom, setSelectedDistanceFrom] = useState(null);

    const { selectedPoint } = props;

    useEffect(() => {
        if (!selectedPoint) return;

        setLoading(true);
        _getSelectedLocation(selectedPoint)
            .then((response) => {
                setLoading(false);
                setSelectedLocation(response);
            });
    }, [selectedPoint]);

    const handleSearch = async (radiusOption, locationData) => {
        if (!radiusOption || !locationData) return;

        const { overlay_address_latitude, overlay_address_longitude } = locationData;

        const geoFilter = {
            ...hiddenFilters.COMPANY.COMPANY_ADDRESS_GEO_RADIUS,
            value: {
                latitude: overlay_address_latitude,
                longitude: overlay_address_longitude,
                radius: radiusOption.value,
            }
        };

        dispatch(setFilterValue({
            id: geoFilter.id,
            ...geoFilter
        }));

        dispatch(countUniverse({
            universeKey: currentUniverse,
            isMapEnabled: targetUniverse.toggleMap
        }));

        dispatch(searchUniverse({
            universeKey: currentUniverse
        }));
    };

    if (loading) return (<ExploreMapFlyoutDetailLoadingSpinner />);

    if (selectedLocation) {
        const {
            overlay_name,
            overlay_website,
            overlay_address_delivery_line_1,
            overlay_address_city,
            overlay_address_region,
            overlay_address_postal_code,
            company_stat_contact_cnt,
            company_profile_cnt,
            company_license_cnt,
            company_permit_cnt,
            company_policy_cnt,
        } = selectedLocation;

        const overlay_address_last_line = [overlay_address_city, overlay_address_region].filter(Boolean).join(', ') + (overlay_address_postal_code ? ` ${overlay_address_postal_code}` : '');

        const densityIcons = [
            { attr: 'overlay_website', icon: faGlobe },
            { attr: 'company_email', icon: faEnvelope },
            { attr: 'company_phone', icon: faPhone },
            { attr: 'company_address_delivery_line_1', icon: faMapMarkerAlt },
        ];

        const densityIconsJsx = densityIcons.map((i, idx) => {
            if (selectedLocation[i.attr] && selectedLocation[i.attr] !== 'null') {
                const marginLeft = idx === 0 ? '0px' : '10px';
                if (i.attr === 'overlay_website') {
                    return (
                        <div key={idx} style={{ marginLeft }}>
                            <a href={`${overlay_website}`} target="_blank" rel="noreferrer">
                                <FontAwesomeIcon color={APP_COLORS['orange']} icon={i.icon} style={{ fontSize: '16px' }} />
                            </a>
                        </div>
                    );
                } else {
                    return (
                        <div key={idx} style={{ marginLeft }}>
                            <FontAwesomeIcon color={APP_COLORS['blue']} icon={i.icon} style={{ fontSize: '16px' }} />
                        </div>
                    );
                }
            }
            return null;
        }).filter(Boolean);

        const badgesIcons = [
            { attr: 'company_stat_contact_cnt', value: company_stat_contact_cnt, icon: faUser },
            { attr: 'company_profile_cnt', value: company_profile_cnt, icon: faSatelliteDish },
            { attr: 'company_license_cnt', value: company_license_cnt, icon: faIdBadge },
            { attr: 'company_permit_cnt', value: company_permit_cnt, icon: faCalculator },
            { attr: 'company_policy_cnt', value: company_policy_cnt, icon: faFileCertificate }
        ];

        const badgesIconsJsx = badgesIcons.map((i, idx) => {
            if (parseInt(i.value, 10) > 0) {
                return (
                    <div key={idx} className="selected-point-detail-record-badges-icons-item">
                        <span>
                            <FontAwesomeIcon color={APP_COLORS['blue']} icon={i.icon} style={{ fontSize: '16px' }} />
                            <span style={{ fontSize: '14px', fontWeight: '600' }}> {i.value}</span>
                        </span>
                    </div>
                );
            }
            return null;
        }).filter(Boolean);

        return (
            <Row>
                <Col>
                    <div className="explore-map-flyout-detail">
                        <ExploreMapFlyoutSelectedDetailSection
                            name={overlay_name}
                            address1={overlay_address_delivery_line_1}
                            address2={overlay_address_last_line}
                            densityIconsJsx={densityIconsJsx}
                            detailIconJsx={
                                <Image
                                    className="icon"
                                    src={mapMarkerIcon}
                                    alt="ToolBeltData Map Marker Icon"
                                    fluid
                                />
                            }
                        />
                        <div className="flyout-map-tools-section">
                            <div className="label-text">SEARCH WITHIN</div>
                            <SelectStyled
                                id="selected-location-point-geospatial-search-typeahead"
                                options={options}
                                onChange={(e) => {
                                    setSelectedDistanceFrom(e);
                                    handleSearch(e, selectedLocation);
                                }}
                                value={selectedDistanceFrom}
                                placeholder={'Select a distance ...'}
                                menuPortalTarget={document.body}
                                menuPosition={'absolute'}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                    </div>
                </Col>
            </Row>
        )
    }
    return null;
}

ExploreMapFlyoutDetailSelectedImportListLocation.propTypes = {
    selectedPoint: PropTypes.object,
};

// --- Helper Functions ---
async function _getSelectedLocation(selectedPoint) {
    const { id, list_id } = selectedPoint.properties;

    const results = await getListImportOverlayRecord(list_id, id);
    if (results && results.length > 0) {
        const data = removeCubeTableName(results[0]);
        return { ...data };
    }
    return null;
}